
import { MetaInfo, ScriptPropertyJson } from 'vue-meta'
import { Component, Mixins, Watch } from 'vue-property-decorator'
import generateNearMeUrl from '~/utility/generateNearMeUrl'
import GeolocationMixin from '~/utility/geolocation/components/GeolocationMixin.vue'
import { Distance } from '~/utility/geo/Distance'

@Component<GlampingNearMe>({
  head: function () {
    return this.head()
  },
})
export default class GlampingNearMe extends Mixins(GeolocationMixin) {
  get breadcrumbs() {
    return [{ label: this.$i18n.t('Glamping near me') }]
  }

  async mounted() {
    await this.startGeolocationMachine()
  }

  get defaultDistance() {
    return Distance.new({ mi: this.$config.public.defaultWithinRange })
  }

  @Watch('geolocationState')
  stateChanged() {
    if (this.geolocationState?.value === 'success') {
      this.redirectToSearch()
    }
  }

  redirectToSearch() {
    if (!this.geolocationContext?.point) return
    const url = generateNearMeUrl(
      'glamping',
      this.$route.query,
      this.geolocationContext.point,
    )
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.$router.push({
      path: this.$puLocalePath(url.path),
      query: url.query as Record<string, any>,
    })
  }

  head(): MetaInfo {
    return {
      title: this.$i18n.t('Glamping near me') + ' | Pitchup.com',
      meta: [
        {
          hid: 'description',
          name: 'description',
          property: 'og:description',
          content: this.$i18n.t(
            'Find and book glamping sites near you. With over 80 filters, search glamping pods, camping pods, yurts or dome tents and find your perfect glamping experience on Pitchup.',
          ) as string,
        },
      ],
      link: [
        {
          rel: 'preload',
          as: 'image',
          href: require('@/assets/near-me/glamping-near-me.jpg'),
          imagesrcset: `${require('@/assets/near-me/glamping-near-me.jpg')} 720w, ${require('@/assets/near-me/glamping-near-me-large.jpg')} 2000w`,
          imagesizes: '100vw',
        },
      ],
      script: [
        {
          type: 'application/ld+json',
          body: true,
          json: {
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            name: this.$i18n.t('Glamping near me') as string,
            contentUrl: 'glamping-near-me-large.jpg',
          },
        },
        {
          type: 'application/ld+json',
          body: true,
          json: {
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            name: this.$i18n.t('Camping near me') as string,
            contentUrl: 'near-me-large.jpg',
          },
        },
        this.breadcrumbList,
      ],
    }
  }

  get breadcrumbList(): ScriptPropertyJson {
    const json = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': `https://${this.$config.public.envUrl}/${
              this.$i18n.locale !== 'en-gb' ? this.$i18n.locale + '/' : ''
            }`,
            name: 'Pitchup.com',
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@id': `https://${this.$config.public.envUrl}/${
              this.$i18n.locale !== 'en-gb' ? this.$i18n.locale + '/' : ''
            }glamping/`,
            name: this.$i18n.t('Glamping') as string,
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@id': `https://${this.$config.public.envUrl}${this.$route.path}`,
            name: this.$i18n.t('Glamping near me') as string,
          },
        },
      ],
    }
    return {
      type: 'application/ld+json',
      body: true,
      json,
    }
  }
}
